import type { IconName } from "../components/Icon";

/**
 * KB categories are free strings chosen by admins, so icon / blurb / tint are
 * sourced frontend-side. We keyword-match the category name to a sensible glyph
 * and tint, with a deterministic hashed tint fallback (mirrors ServiceCatalog's
 * iconColorFor) so unknown categories still look intentional.
 */
export type KbTint = "violet" | "green" | "orange" | "blue" | "red";

export interface KbCategoryMeta {
  icon: IconName;
  blurb: string;
  tint: KbTint;
}

const TINTS: KbTint[] = ["violet", "blue", "green", "orange", "red"];

interface Rule {
  test: RegExp;
  meta: KbCategoryMeta;
}

// Most-specific first.
const RULES: Rule[] = [
  { test: /security|mfa|phish|2fa|password|sign[- ]?in|account|login/, meta: { icon: "lock", tint: "violet", blurb: "Passwords, sign-in, and keeping your account safe." } },
  { test: /microsoft|m365|office|outlook|teams|onedrive|sharepoint|email|mail/, meta: { icon: "mail", tint: "blue", blurb: "Outlook, Teams, OneDrive and shared mailboxes." } },
  { test: /print|device|hardware|laptop|monitor|peripheral/, meta: { icon: "printer", tint: "orange", blurb: "Printers, performance and everyday device fixes." } },
  { test: /vpn|wi[- ]?fi|network|connect|remote|internet/, meta: { icon: "wifi", tint: "red", blurb: "VPN, Wi-Fi and working away from the office." } },
  { test: /shield|policy|data|privacy|compliance/, meta: { icon: "shield", tint: "green", blurb: "Security, policy and handling data safely." } },
];

function tintFor(seed: string): KbTint {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return TINTS[Math.abs(hash) % TINTS.length];
}

export function kbCategoryMeta(category: string): KbCategoryMeta {
  const hay = (category || "").toLowerCase();
  for (const rule of RULES) {
    if (rule.test.test(hay)) return rule.meta;
  }
  return { icon: "book", tint: tintFor(category || "kb"), blurb: "" };
}
